import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { auth } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Lock, User, MapPin, Phone, Building2, UserCircle, KeyRound, ArrowLeft, UserPlus, Shield } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [mode, setMode] = useState('login'); // login, 2fa-verify, register-admin, register-agent, forgot-password, reset-password
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    name: '',
    address: '',
    phone: '',
    resetCode: '',
    newPassword: '',
    confirmPassword: '',
    twoFACode: ''
  });
  const [resetEmail, setResetEmail] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState({ email: '', password: '' });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (mode === 'login') {
        // First check if 2FA is required
        const check2FA = await axios.post(`${API}/api/auth/check-2fa-required`, null, {
          params: { email: formData.email }
        });
        
        if (check2FA.data.requires_2fa) {
          // Send 2FA code and switch to verification mode
          await axios.post(`${API}/api/auth/2fa/send-login-code`, null, {
            params: { email: formData.email }
          });
          setPendingCredentials({ email: formData.email, password: formData.password });
          setMode('2fa-verify');
          toast.info('Enter the verification code sent to your email');
          setLoading(false);
          return;
        }
        
        response = await auth.login({ email: formData.email, password: formData.password });
      } else if (mode === '2fa-verify') {
        response = await axios.post(`${API}/api/auth/login-with-2fa`, null, {
          params: {
            email: pendingCredentials.email,
            password: pendingCredentials.password,
            code: formData.twoFACode
          }
        });
      } else if (mode === 'register-agent') {
        response = await auth.registerAgent({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          address: formData.address,
          phone: formData.phone
        });
      } else {
        response = await auth.register({
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
      }

      const { access_token, user } = response.data;
      
      // If agent has a preferred division, set it in localStorage
      if (user.role === 'agent' && user.preferred_division) {
        localStorage.setItem('businessUnit', user.preferred_division);
      }
      
      login(user, access_token);
      toast.success(`Welcome${mode === 'login' || mode === '2fa-verify' ? ' back' : ''}, ${user.name}!`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await auth.requestPasswordReset({ email: formData.email });
      toast.success(response.data.message);
      setResetEmail(formData.email);
      setMode('reset-password');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await auth.resetPassword({
        email: resetEmail,
        reset_code: formData.resetCode,
        new_password: formData.newPassword
      });
      toast.success(response.data.message);
      setMode('login');
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', address: '', phone: '', resetCode: '', newPassword: '', confirmPassword: '', twoFACode: '' });
    setPendingCredentials({ email: '', password: '' });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.9)), url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200')`
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h2 className="text-white text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
            Utmost Financial Inc.
          </h2>
          <p className="text-slate-300 text-lg max-w-md">
            Track commissions, manage agents, and generate statements across Mortgage, Insurance & Investment divisions.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-xl" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <img src="/logo.png" alt="Utmost Financial Inc." className="h-24 mx-auto mb-4" />
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
              {mode === 'login' ? 'Welcome Back' : 
               mode === '2fa-verify' ? 'Verify Your Identity' :
               mode === 'register-agent' ? 'Agent Registration' : 
               mode === 'forgot-password' ? 'Forgot Password' :
               mode === 'reset-password' ? 'Reset Password' :
               'Admin Registration'}
            </CardTitle>
            <CardDescription>
              {mode === 'login' ? 'Sign in to your account' : 
               mode === '2fa-verify' ? 'Enter the verification code sent to your email' :
               mode === 'register-agent' ? 'Create your agent account to view your commissions' : 
               mode === 'forgot-password' ? 'Enter your email to receive a reset code' :
               mode === 'reset-password' ? 'Enter the reset code sent to your email' :
               'Create an admin account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === '2fa-verify' ? (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Shield className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="twoFACode">Verification Code</Label>
                    <Input
                      id="twoFACode"
                      type="text"
                      maxLength={6}
                      data-testid="2fa-code-input"
                      placeholder="Enter 6-digit code"
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      value={formData.twoFACode}
                      onChange={(e) => setFormData({ ...formData, twoFACode: e.target.value.replace(/\D/g, '') })}
                      required
                    />
                    <p className="text-xs text-slate-500 text-center">
                      The code was sent to {pendingCredentials.email}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    data-testid="verify-2fa-submit-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
                    disabled={loading || formData.twoFACode.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setFormData({ ...formData, twoFACode: '' }); }}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to login
                  </button>
                </div>
              </>
            ) : mode === 'login' ? (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        data-testid="email-input"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        data-testid="password-input"
                        placeholder="••••••••"
                        className="pl-10"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => { setMode('forgot-password'); resetForm(); }}
                      className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-btn"
                    className="w-full bg-slate-900 hover:bg-slate-800 h-11"
                    disabled={loading}
                  >
                    {loading ? 'Please wait...' : 'Sign In'}
                  </Button>
                </form>

                <div className="mt-6 space-y-3">
                  <p className="text-center text-sm text-slate-500">Don&apos;t have an account?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setMode('register-agent'); resetForm(); }}
                      className="flex items-center gap-2"
                      data-testid="register-agent-btn"
                    >
                      <UserCircle className="w-4 h-4" />
                      Agent Sign Up
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setMode('register-admin'); resetForm(); }}
                      className="flex items-center gap-2"
                      data-testid="register-admin-btn"
                    >
                      <Building2 className="w-4 h-4" />
                      Admin Sign Up
                    </Button>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-slate-100 rounded-lg text-xs text-slate-600">
                  <p className="font-medium mb-1">Demo Admin Credentials:</p>
                  <p>Email: admin@utmostfinancial.ca</p>
                  <p>Password: admin123</p>
                </div>
              </>
            ) : (mode === 'register-admin' || mode === 'register-agent') ? (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="name"
                        data-testid="name-input"
                        placeholder="John Doe"
                        className="pl-10"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        data-testid="email-input"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        data-testid="password-input"
                        placeholder="Min 6 characters"
                        className="pl-10"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                      />
                    </div>
                  </div>

                  {mode === 'register-agent' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="address">Address</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="address"
                            data-testid="address-input"
                            placeholder="123 Main St, City, Province"
                            className="pl-10"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="phone"
                            data-testid="phone-input"
                            placeholder="(204) 555-0123"
                            className="pl-10"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <Button
                    type="submit"
                    data-testid="submit-btn"
                    className={`w-full h-11 ${mode === 'register-agent' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                    disabled={loading}
                  >
                    {loading ? 'Please wait...' : mode === 'register-agent' ? 'Create Agent Account' : 'Create Admin Account'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); resetForm(); }}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    Already have an account?{' '}
                    <span className="font-semibold text-emerald-600">Sign in</span>
                  </button>
                </div>

                {mode === 'register-agent' && (
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg text-xs text-emerald-700">
                    <p className="font-medium mb-1">Agent Account Benefits:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>View your personal commissions</li>
                      <li>Track your pending payments</li>
                      <li>Download your statements</li>
                    </ul>
                  </div>
                )}
              </>
            ) : mode === 'forgot-password' ? (
              <>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reset-email"
                        type="email"
                        data-testid="reset-email-input"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="request-reset-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
                    disabled={loading}
                  >
                    {loading ? 'Please wait...' : 'Send Reset Code'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); resetForm(); }}
                    className="text-sm text-slate-600 hover:text-slate-900 flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </button>
                </div>
              </>
            ) : mode === 'reset-password' ? (
              <>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700 mb-4">
                    <p>A reset code has been sent. Enter it below along with your new password.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reset-code">Reset Code</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reset-code"
                        type="text"
                        data-testid="reset-code-input"
                        placeholder="Enter 6-digit code"
                        className="pl-10 text-center tracking-widest font-mono text-lg"
                        maxLength={6}
                        value={formData.resetCode}
                        onChange={(e) => setFormData({ ...formData, resetCode: e.target.value.replace(/\D/g, '') })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="new-password"
                        type="password"
                        data-testid="new-password-input"
                        placeholder="Enter new password"
                        className="pl-10"
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirm-password"
                        type="password"
                        data-testid="confirm-password-input"
                        placeholder="Confirm new password"
                        className="pl-10"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="reset-password-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
                    disabled={loading}
                  >
                    {loading ? 'Please wait...' : 'Reset Password'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); resetForm(); }}
                    className="text-sm text-slate-600 hover:text-slate-900 flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </button>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Referrer Portal Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/referrer/login"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
          >
            <UserPlus className="w-4 h-4" />
            Referral Partner Portal
          </Link>
          <p className="text-sm text-slate-500 mt-1">Earn $200 per successful referral!</p>
        </div>
      </div>
    </div>
  );
}
